import React, { useRef, useState } from 'react';
import { Paperclip, Plus, Search, Trash2 } from 'lucide-react';
import { useToast } from '../../../../../hooks/useToast';
import { Skeleton } from '../../../../../components/shared/Skeleton/Skeleton';
import { JournalLine } from '../../../../../services/JournalEntriesService';
import { formatEuro } from '../masters/financeMasters';
import './FinanceForms.css';

// Piezas comunes de los formularios de documentos de Finanzas

export interface SearchOption {
  value: string;
  label: string;
  sub?: string;
}

interface SearchSelectProps {
  options: SearchOption[];
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  emptyText?: string;
  disabled?: boolean;
  // Texto que se muestra si el valor no es ninguna de las opciones
  fallbackLabel?: string;
  // Criterio de búsqueda propio; por defecto, "contiene" en label y sub
  matches?: (option: SearchOption, query: string) => boolean;
}

// Campo con buscador: se escribe para filtrar y se elige una opción de la lista
export const SearchSelect = ({
  options,
  value,
  onChange,
  placeholder = 'Buscar...',
  emptyText = 'Sin resultados',
  disabled = false,
  fallbackLabel = '',
  matches
}: SearchSelectProps) => {
  const [query, setQuery] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const selected = options.find(o => o.value === value);
  const q = query.toLowerCase();
  const filtered = options
    .filter(o => matches
      ? matches(o, q)
      : o.label.toLowerCase().includes(q) || (o.sub || '').toLowerCase().includes(q))
    .slice(0, 50);

  return (
    <div className="fin-search-select">
      <Search size={12} className="fin-search-select__icon" />
      <input
        ref={inputRef}
        type="text"
        className="sidebar-form-input fin-search-select__input"
        value={isOpen ? query : selected ? selected.label : fallbackLabel}
        placeholder={selected && !isOpen ? selected.label : placeholder}
        disabled={disabled}
        onFocus={() => { setQuery(''); setIsOpen(true); }}
        onBlur={() => setIsOpen(false)}
        onChange={(e) => setQuery(e.target.value)}
      />
      {isOpen && (
        <div className="fin-search-select__list">
          {filtered.length === 0 ? (
            <div className="fin-search-select__empty">{emptyText}</div>
          ) : (
            filtered.map(o => (
              <button
                key={o.value}
                type="button"
                className={`fin-search-select__option ${o.value === value ? 'selected' : ''}`}
                // mousedown en vez de click: el blur del input cerraría la lista antes del click
                onMouseDown={(e) => {
                  e.preventDefault();
                  onChange(o.value);
                  setIsOpen(false);
                  // Sin foco, el siguiente clic vuelve a disparar onFocus y abre la lista
                  inputRef.current?.blur();
                }}
              >
                <span>{o.label}</span>
                {o.sub && <span className="fin-search-select__sub">{o.sub}</span>}
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
};

export interface DocumentLine {
  id: string;
  productId: string;
  description: string;
  quantity: number;
  price: number;
  vat: number;
}

export interface LineProduct {
  id: string;
  name: string;
  sku: string;
  price: number;
  vat: number;
  // Existencias; sin definir = no se conocen y no se controlan
  stock?: number;
}

export const VAT_RATES = [4, 10, 21, 0];

export const newLine = (): DocumentLine => ({
  id: String(Date.now() + Math.random()),
  productId: '',
  description: '',
  quantity: 1,
  price: 0,
  vat: 4
});

export const linesSubtotal = (lines: DocumentLine[]) => lines.reduce((s, l) => s + l.quantity * l.price, 0);
export const linesVat = (lines: DocumentLine[]) => lines.reduce((s, l) => s + (l.quantity * l.price * l.vat) / 100, 0);

interface DocumentLinesEditorProps {
  lines: DocumentLine[];
  onChange: (lines: DocumentLine[]) => void;
  products: LineProduct[];
  privacyMode: boolean;
  // Líneas copiadas de una factura: sin libro enlazado, el artículo es su descripción
  descriptionAsArticle?: boolean;
  // Controla las existencias: no deja elegir un artículo sin stock ni vender más de lo que hay
  checkStock?: boolean;
}

// Unidades de un artículo ya pedidas en otras líneas del documento
const unitsInOtherLines = (lines: DocumentLine[], lineId: string, productId: string) =>
  lines.reduce((sum, l) => (l.id !== lineId && l.productId === productId ? sum + l.quantity : sum), 0);

// Líneas de artículos del documento, con buscador de artículo y "+ AÑADIR LÍNEA"
export const DocumentLinesEditor = ({ lines, onChange, products, privacyMode, descriptionAsArticle = false, checkStock = false }: DocumentLinesEditorProps) => {
  const { notify } = useToast();
  const updateLine = (id: string, patch: Partial<DocumentLine>) =>
    onChange(lines.map(l => (l.id === id ? { ...l, ...patch } : l)));

  const selectProduct = (id: string, productId: string) => {
    const p = products.find(pr => pr.id === productId);
    if (checkStock && p && p.stock !== undefined) {
      const available = p.stock - unitsInOtherLines(lines, id, productId);
      if (available <= 0) {
        notify('Sin stock', `"${p.name}" no tiene existencias disponibles.`, 'warning');
        return;
      }
    }
    updateLine(id, p ? { productId, description: p.name, price: p.price, vat: p.vat } : { productId });
  };

  const productOptions = products.map(p => ({
    value: p.id,
    label: p.name,
    sub: checkStock && p.stock !== undefined ? `${p.sku} · ${p.stock > 0 ? `Stock: ${p.stock}` : 'SIN STOCK'}` : p.sku
  }));

  // Con control de stock la cantidad no puede pasar de lo disponible
  const changeQuantity = (line: DocumentLine, wanted: number) => {
    const p = checkStock && line.productId ? products.find(pr => pr.id === line.productId) : undefined;
    if (p && p.stock !== undefined) {
      const available = p.stock - unitsInOtherLines(lines, line.id, line.productId);
      if (wanted > available) {
        notify('Sin stock', `"${p.name}": solo quedan ${Math.max(available, 0)} unidad(es) disponibles.`, 'warning');
        updateLine(line.id, { quantity: Math.max(available, 1) });
        return;
      }
    }
    updateLine(line.id, { quantity: wanted });
  };

  return (
    <div className="fin-lines">
      <div className="fin-lines__header">
        <span>Líneas de artículos / servicios</span>
        <button type="button" className="fin-lines__add" onClick={() => onChange([...lines, newLine()])}>
          <Plus size={12} /> AÑADIR LÍNEA
        </button>
      </div>

      <div className="fin-lines__row fin-lines__row--head">
        <span>Artículo</span>
        <span>Descripción</span>
        <span className="center">Cant.</span>
        <span className="right">Precio (€)</span>
        <span>IVA</span>
        <span className="right">Importe</span>
        <span />
      </div>

      {lines.map(line => (
        <div key={line.id} className="fin-lines__row">
          <SearchSelect
            options={productOptions}
            value={line.productId}
            onChange={(productId) => selectProduct(line.id, productId)}
            placeholder="Buscar artículo..."
            emptyText="No hay artículos"
            fallbackLabel={descriptionAsArticle ? line.description : ''}
          />
          <input
            type="text"
            required
            className="sidebar-form-input"
            value={line.description}
            onChange={(e) => updateLine(line.id, { description: e.target.value })}
            placeholder="Descripción de la línea"
          />
          <input
            type="number"
            min={1}
            step={1}
            className="sidebar-form-input center"
            value={line.quantity}
            onChange={(e) => changeQuantity(line, parseInt(e.target.value, 10) || 1)}
          />
          <input
            type="number"
            min={0}
            step="0.01"
            className="sidebar-form-input right"
            value={line.price}
            onChange={(e) => updateLine(line.id, { price: parseFloat(e.target.value) || 0 })}
          />
          <select
            className="sidebar-form-input"
            value={line.vat}
            onChange={(e) => updateLine(line.id, { vat: parseInt(e.target.value, 10) || 0 })}
          >
            {VAT_RATES.map(r => <option key={r} value={r}>{r}%</option>)}
          </select>
          <span className="fin-lines__amount">{formatEuro(line.quantity * line.price, privacyMode)}</span>
          <button
            type="button"
            className="fin-lines__remove"
            title="Eliminar línea"
            disabled={lines.length === 1}
            onClick={() => onChange(lines.filter(l => l.id !== line.id))}
          >
            <Trash2 size={13} />
          </button>
        </div>
      ))}
    </div>
  );
};

// Asiento que se va a generar, visible antes de contabilizar
export const JournalPreview = ({ lines, privacyMode }: { lines: JournalLine[]; privacyMode: boolean }) => {
  const debit = lines.filter(l => l.debit > 0.005);
  const credit = lines.filter(l => l.credit > 0.005);

  return (
    <div className="fin-journal-preview">
      <span className="fin-journal-preview__title">Asiento contable que se generará (Libro Diario)</span>
      <div className="fin-journal-preview__grid">
        <div className="fin-journal-preview__side">
          <strong className="fin-journal-preview__label debit">DEBE</strong>
          {debit.map((l, i) => (
            <div key={i} className="fin-journal-preview__line">
              <span>{l.account} {l.accountName}</span>
              <strong>{formatEuro(l.debit, privacyMode)}</strong>
            </div>
          ))}
        </div>
        <div className="fin-journal-preview__side">
          <strong className="fin-journal-preview__label credit">HABER</strong>
          {credit.map((l, i) => (
            <div key={i} className="fin-journal-preview__line">
              <span>{l.account} {l.accountName}</span>
              <strong>{formatEuro(l.credit, privacyMode)}</strong>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

const formatSize = (bytes: number) =>
  bytes < 1024 * 1024 ? `${Math.max(1, Math.round(bytes / 1024))} KB` : `${(bytes / (1024 * 1024)).toFixed(1)} MB`;

interface AttachmentsFieldProps {
  files: File[];
  onChange: (files: File[]) => void;
}

// Adjuntos del documento: abre el selector de archivos del sistema
export const AttachmentsField = ({ files, onChange }: AttachmentsFieldProps) => {
  const inputRef = useRef<HTMLInputElement>(null);

  const handleSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selected = e.target.files ? Array.from(e.target.files) : [];
    const isNew = (f: File) => !files.some(x => x.name === f.name && x.size === f.size);
    onChange([...files, ...selected.filter(isNew)]);
    // Permite volver a elegir el mismo archivo después de quitarlo
    e.target.value = '';
  };

  return (
    <div className="sidebar-form-group">
      <label className="sidebar-form-label">Documentos adjuntos</label>
      <input ref={inputRef} type="file" multiple className="fin-attach__input" onChange={handleSelect} />
      <button type="button" className="fin-attach__btn" onClick={() => inputRef.current && inputRef.current.click()}>
        <Paperclip size={12} /> ADJUNTAR ARCHIVOS
      </button>
      {files.length > 0 && (
        <div className="fin-attach__list">
          {files.map((file, idx) => (
            <div key={`${file.name}-${idx}`} className="fin-attach__item">
              <Paperclip size={10} />
              <span className="fin-attach__name">{file.name}</span>
              <span className="fin-attach__size">{formatSize(file.size)}</span>
              <button
                type="button"
                className="fin-lines__remove"
                title="Quitar"
                onClick={() => onChange(files.filter((_, i) => i !== idx))}
              >
                <Trash2 size={11} />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// Fechas en hora local (toISOString pasaría a UTC y podría restar un día)
export const toIsoDate = (d: Date) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

export const today = () => toIsoDate(new Date());

export const addDays = (iso: string, days: number) => {
  const [y, m, d] = iso.split('-').map(Number);
  return toIsoDate(new Date(y, m - 1, d + days));
};

export const formatDate = (iso: string) => {
  const [y, m, d] = iso.split('-');
  return d && m && y ? `${d}/${m}/${y}` : iso;
};

// Datos con los que se abre una factura nueva (p. ej. al convertir un presupuesto)
export interface InvoicePrefill {
  clientId: string;
  reference: string;
  lines: DocumentLine[];
  notes: string;
}

// Esqueleto mientras cargan los datos del formulario
export const FormSkeleton = () => (
  <div className="fin-doc">
    <div className="fin-doc__grid">
      {Array.from({ length: 6 }).map((_, i) => (
        <div key={i} className="skeleton-block">
          <Skeleton width="40%" height="9px" />
          <Skeleton width="100%" height="30px" />
        </div>
      ))}
    </div>
    <Skeleton width="100%" height="80px" />
  </div>
);
